import { message } from "antd";
import { debounce } from "lodash-es";
import { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import { useNavigate } from "react-router-dom-v5-compat";

import { BdsText } from "@buildertrend/components";

import { BTSelectItem } from "types/apiResponse/apiResponse";

import { showApixErrorMessage } from "utilities/api/apix-handler";
import { getEditModeLink } from "utilities/editMode/edit.utility";
import { type KeyOfOrString } from "utilities/type/PropsOfType";
import { useRouteProps } from "utilities/url/url";

import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { BTSelectEditable } from "commonComponents/btWrappers/BTSelectEditable/BTSelectEditable";
import {
    getDetailPath,
    type IDetailProps,
} from "commonComponents/utilities/CollectionEditable/CollectionEditable";

import {
    type AvailableEntitiesResponseEntitiesItem,
    type EntityRecordResponse,
    useEntityRecordsAvailableOnJobGetV2,
    useEntityRecordsLinkedEntitiesBySourceExternalIdPutV2,
} from "handlers";

import { getEntityDetailPathPattern, getEntityDetailRoute } from "./EntityLinkRoutes.utilities";
import {
    type IEntityDetailRenderProps,
    type IEntityLinkSelectorEditProps,
    type IEntitySelectItemExtraData,
} from "./EntityLinkSelector.types";
import {
    updateAvailableEntitiesCache,
    updateLinkedEntitiesCache,
} from "./EntityLinkSelector.utilities";

/**
 * Edit mode component for EntityLinkSelector
 * Handles edit-mode-specific logic including available entities fetching, mutations, and BTSelectEditable rendering
 */
export const EntityLinkSelectorEdit = <FormValues, ExtraData extends IEntitySelectItemExtraData>(
    props: IEntityLinkSelectorEditProps<FormValues, ExtraData>,
) => {
    const {
        sourceExternalId,
        sourceEntityType,
        targetEntityType,
        jobId,
        linkedIds,
        isLoadingLinked,
        canAdd = false,
        canEdit = false,
        fieldPath = "entities" as KeyOfOrString<FormValues>,
        useBds = true,
        renderDetail,
        entityNamePlural,
        entityName,
        customItemRender,
        helperText,
        dropdownClassName,
        queryKeyEntityTypes,
        onPersistLinksOverride,
    } = props;

    const location = useLocation();
    const { match } = useRouteProps();
    const navigate = useNavigate();

    // Fetch available entities for this job (edit mode only)
    const {
        data: availableEntitiesData,
        isLoading: isLoadingAvailable,
        error: availableError,
        dataUpdatedAt: availableEntitiesUpdatedAt,
    } = useEntityRecordsAvailableOnJobGetV2({
        jobParam: jobId,
        entityType: targetEntityType,
    });

    // Convert available entities to BTSelectItem format from API data
    const availableEntitiesFromApi = useMemo<BTSelectItem<ExtraData>[]>(() => {
        if (!availableEntitiesData?.data.entities) return [];

        return availableEntitiesData.data.entities.map(
            (entity) =>
                new BTSelectItem<ExtraData>({
                    id: entity.id ?? "",
                    name: entity.title ?? "",
                    selected: false,
                    extraData: {
                        id: entity.entityId ?? 0,
                        status: entity.status,
                    } as ExtraData,
                }),
        );
    }, [availableEntitiesData]);

    // Create a lookup Map for entity lookups by ID from API data
    const entityLookupById = useMemo(() => {
        const map = new Map<string, AvailableEntitiesResponseEntitiesItem>();
        availableEntitiesData?.data.entities?.forEach((entity) => {
            if (entity.id) {
                map.set(entity.id, entity);
            }
        });
        return map;
    }, [availableEntitiesData]);

    // Helper method to lookup entities by IDs, filtering out null results
    const getEntitiesByIds = useCallback(
        (ids: string[]): AvailableEntitiesResponseEntitiesItem[] => {
            return ids
                .map((id) => entityLookupById.get(id))
                .filter((entity): entity is NonNullable<typeof entity> => entity != null);
        },
        [entityLookupById],
    );

    // Only show selections once available entities are loaded to prevent GUIDs from appearing
    const displayedLinkedIds = availableEntitiesFromApi.length > 0 ? linkedIds : [];

    // Mutation for syncing links with automatic rollback on error
    const { mutate: syncLinks } = useEntityRecordsLinkedEntitiesBySourceExternalIdPutV2({
        mutation: {
            onMutate: () => {
                // Notify if needed
                if (props.successMessage) {
                    message.success(props.successMessage);
                }
                // Capture previous IDs for potential rollback
                return { previousIds: linkedIds };
            },
            onError: (error, _variables, context) => {
                // Rollback cache on error
                if (context?.previousIds) {
                    const previousEntities = getEntitiesByIds(context.previousIds);
                    updateLinkedEntitiesCache(
                        sourceExternalId,
                        targetEntityType,
                        previousEntities,
                        queryKeyEntityTypes,
                    );
                }
                showApixErrorMessage(error as Error);
            },
        },
    });

    const fieldPathString = String(fieldPath);

    const editModeModalConfig: IModalConfiguration = useMemo(
        () => ({
            beforeClose: () => navigate(-1),
            parentRoute: match.url,
        }),
        [match.url, navigate],
    );

    const syncEntityLinks = useCallback(
        (value: string[]) => {
            // Mutation will handle optimistic update and rollback
            syncLinks({
                sourceExternalId,
                data: {
                    sourceEntityType,
                    targetLinks: [
                        {
                            targetEntityType,
                            targetExternalIds: value,
                        },
                    ],
                },
                params: {
                    jobParam: jobId,
                },
            });
        },
        [sourceExternalId, sourceEntityType, targetEntityType, jobId, syncLinks],
    );

    // Debounced version of sync - waits 300ms after last change before calling API
    const debouncedSyncLinks = useMemo(() => debounce(syncEntityLinks, 300), [syncEntityLinks]);

    useEffect(() => {
        return () => {
            debouncedSyncLinks.cancel();
        };
    }, [debouncedSyncLinks]);

    const handleEntityLinksChange = useCallback(
        (_field: string, value: string[]) => {
            // Look up full entity data using helper method
            const linkedEntities = getEntitiesByIds(value);

            // Update the linked entities cache so other components using the same query update
            updateLinkedEntitiesCache(
                sourceExternalId,
                targetEntityType,
                linkedEntities,
                queryKeyEntityTypes,
            );

            if (onPersistLinksOverride) {
                // Mass-link mode: parent owns persistence; skip the per-source PUT
                onPersistLinksOverride(targetEntityType, value);
                return;
            }

            // Debounce the API call
            debouncedSyncLinks(value);
        },
        [
            sourceExternalId,
            targetEntityType,
            debouncedSyncLinks,
            getEntitiesByIds,
            queryKeyEntityTypes,
            onPersistLinksOverride,
        ],
    );

    const updateLinkedIds = useCallback(
        (itemId: string, shouldAdd: boolean) => {
            const isAlreadyLinked = linkedIds.includes(itemId);

            if (shouldAdd && !isAlreadyLinked) {
                handleEntityLinksChange(fieldPathString, [...linkedIds, itemId]);
            } else if (!shouldAdd && isAlreadyLinked) {
                handleEntityLinksChange(
                    fieldPathString,
                    linkedIds.filter((id) => id !== itemId),
                );
            }
        },
        [linkedIds, handleEntityLinksChange, fieldPathString],
    );

    const addNewLinkedEntity = useCallback(
        (entity: EntityRecordResponse) => {
            // Update available entities cache with new entity prepended
            const currentAvailable = availableEntitiesData?.data.entities ?? [];
            updateAvailableEntitiesCache(jobId, targetEntityType, [entity, ...currentAvailable]);

            // Update linked entities cache with new entity prepended
            const existingLinked = getEntitiesByIds(linkedIds);
            updateLinkedEntitiesCache(
                sourceExternalId,
                targetEntityType,
                [entity, ...existingLinked],
                queryKeyEntityTypes,
            );

            if (onPersistLinksOverride) {
                onPersistLinksOverride(targetEntityType, [...linkedIds, entity.id]);
                return;
            }

            // Sync with API
            debouncedSyncLinks([...linkedIds, entity.id]);
        },
        [
            sourceExternalId,
            targetEntityType,
            jobId,
            getEntitiesByIds,
            linkedIds,
            debouncedSyncLinks,
            availableEntitiesData,
            queryKeyEntityTypes,
            onPersistLinksOverride,
        ],
    );

    // Updates both query caches when an entity is edited from the modal.
    // Linked cache: ensures the RelatedItemTag list reflects updated data (title, status, etc.)
    // Available cache: ensures the dropdown items reflect updated data
    const updateCachesOnEdit = useCallback(
        (entity: EntityRecordResponse) => {
            // Update linked entities cache with updated entity
            const currentLinked = getEntitiesByIds(linkedIds);
            const updatedLinked = currentLinked.map((e) => (e.id === entity.id ? entity : e));
            updateLinkedEntitiesCache(
                sourceExternalId,
                targetEntityType,
                updatedLinked,
                queryKeyEntityTypes,
            );

            // Update available entities cache with updated entity
            const currentAvailable = availableEntitiesData?.data.entities ?? [];
            const updatedAvailable = currentAvailable.map((e) => (e.id === entity.id ? entity : e));
            updateAvailableEntitiesCache(jobId, targetEntityType, updatedAvailable);
        },
        [
            sourceExternalId,
            targetEntityType,
            jobId,
            getEntitiesByIds,
            linkedIds,
            availableEntitiesData,
            queryKeyEntityTypes,
        ],
    );

    const getEntityDetailEditRoute = useCallback(
        (entityId: number) => {
            return getEditModeLink(
                getDetailPath(
                    match,
                    getEntityDetailRoute(targetEntityType, entityId, jobId),
                    location.search,
                ),
            );
        },
        [match, targetEntityType, jobId, location.search],
    );

    const renderEntityDetail = useCallback(
        (detailProps: IDetailProps<BTSelectItem<ExtraData>>) => {
            const params = detailProps.match.params;
            const rawId = params["id"];

            if (!rawId) {
                throw new Error("Invalid ID parameter");
            }

            const id = Number(rawId);

            // Override CollectionEditable's add/edit/delete with React Query cache updates.
            // We manage entity data through query caches (linked + available) rather than
            // CollectionEditable's internal state, so onTreeDataChange is a no-op.
            const entityDetailProps: IEntityDetailRenderProps<ExtraData> = {
                ...detailProps,
                add: (entity: EntityRecordResponse) => {
                    addNewLinkedEntity(entity);
                },
                edit: (entity: EntityRecordResponse) => {
                    updateCachesOnEdit(entity);
                },
                delete: (entity: EntityRecordResponse) => {
                    updateLinkedIds(entity.id, false);
                },
                jobId,
                modalConfig: editModeModalConfig,
                id,
            };

            return renderDetail(entityDetailProps);
        },
        [
            jobId,
            editModeModalConfig,
            renderDetail,
            updateLinkedIds,
            addNewLinkedEntity,
            updateCachesOnEdit,
        ],
    );

    const hasNoEntities = availableEntitiesFromApi.length === 0;
    const isLoading = isLoadingAvailable || isLoadingLinked;
    const hasError = !!availableError;

    // Edit mode: Show selector only (tags hidden during edit)
    // Key forces remount when available entities query data changes, preventing
    // the dropdown from automatically closing on the first click after a cache update.
    const selectorElement = (
        <BTSelectEditable<FormValues, ExtraData>
            key={`entitySelector-${availableEntitiesUpdatedAt}`}
            id={fieldPath}
            data-testid={`${fieldPathString}-selector`}
            multiple
            value={displayedLinkedIds}
            treeData={availableEntitiesFromApi}
            detailPath={getEntityDetailPathPattern(targetEntityType)}
            onTreeDataChange={() => {}} // No-op: entity data managed via React Query caches, not CollectionEditable state
            onChange={handleEntityLinksChange}
            onBlur={() => {}}
            onFocus={() => {}}
            canAdd={canAdd}
            canEdit={canEdit}
            useBds={useBds}
            loading={isLoading}
            disabled={isLoading || !!hasError}
            noTreeDataPlaceholder={""}
            maxTagCount="responsive"
            addRoute={getEntityDetailEditRoute(0)}
            editRoute={(item) => getEntityDetailEditRoute(item.extraData?.id ?? 0)}
            renderDetail={renderEntityDetail}
            customItemRender={customItemRender}
            dropdownClassName={dropdownClassName}
        />
    );

    return (
        <div className="margin-top-sm">
            <BTPopover
                content={`No ${entityNamePlural} available. Create a new ${entityName} to link it.`}
                disabled={!hasNoEntities || isLoading}
            >
                {selectorElement}
            </BTPopover>
            {helperText && <BdsText text={helperText} size="normal-sm" className="helper-text" />}
        </div>
    );
};
